import { useContext, useState } from 'react'
import { Heading3, StrongText, SubParagraph } from '@entur/typography'
import { OverflowMenu, OverflowMenuItem } from '@entur/menu'
import { GridContainer, GridItem } from '@entur/grid'
import { BannerAlertBox, useToast } from '@entur/alert'
import { AddIcon, ClosedLockIcon, HorizontalDotsIcon, OpenedLockIcon, ValidTicketIcon } from '@entur/icons'
import { colors, space } from '@entur/tokens'
import { useEmployee } from '../../contexts/employee-context'
import { TicketRightCard } from './cards/ticket-right-card'
import { BlueModal } from '../modal/blue-modal'
import { NewTicketRight } from './new-ticket-right'
import { DisabledContext } from '../../contexts/disable-context'
import {
  adjustTicketRightsWithDraft,
  createTemporaryTicketRight,
  findActiveTicketRights,
  getYearForTicketRight,
  ticketTypeLabel,
} from '../../utils/ticket-rights-utils'
import { Employee, FetchState, TicketTypePreference, YearlyCategory } from '../../api-clients/api-types'
import { useEmployeePatch } from '../../hooks/patch-employee'
import { useGenerateTicketRights } from '../../hooks/use-ticket-rights'
import { useApi } from '../../api-clients/client-hooks'
import { patchCustomerPreference } from '../../api-clients/eba-clients'
import { MenuBtn } from '../overflow-menu-icon-btn/overflow-menu-icon-btn'
import { isSeniorCitizenByAge } from '../../utils/date-utils'
import CopyButton from '../copy-button/copy-button'
import { TicketRightWithDraft } from '../../types'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'
import { compareAsc } from 'date-fns'

const year = new Date().getFullYear()
const nextYear = year + 1

const calculateActiveTicketRights = (employee: Employee, category?: YearlyCategory): TicketRightWithDraft[] => {
  let activeTicketRights = findActiveTicketRights(employee.ticketRights)

  if (activeTicketRights?.length === 0 && category) {
    activeTicketRights = [year, nextYear]?.map((it) =>
      createTemporaryTicketRight(it, employee.id, employee.ticketHolderCode)
    )
  }
  const activeYearlyTickets = activeTicketRights?.filter((it) => !it.manualOrder)
  if (activeYearlyTickets?.length === 1) {
    const forYear = getYearForTicketRight(activeYearlyTickets[0]) === year ? nextYear : year
    if (forYear === year)
      activeTicketRights?.unshift(createTemporaryTicketRight(forYear, employee.id, employee.ticketHolderCode))
    else activeTicketRights?.push(createTemporaryTicketRight(forYear, employee.id, employee.ticketHolderCode))
  }
  return adjustTicketRightsWithDraft(activeTicketRights, employee?.draftEmployee?.ticketRights).sort((a, b) =>
    compareAsc(a.ticketRight.validFrom, b.ticketRight.validFrom)
  )
}

export const TicketRights = () => {
  const { hasSensitiveAccess } = useClientInfo()
  const { employee, setCustomerPreferences } = useEmployee()
  const { disabled } = useContext(DisabledContext)
  const { readOnly } = useContext(ReadOnlyContext)
  const { addToast } = useToast()
  const category = employee.categories.find((it) => it.year === year)
  const [isOpenBlueModal, setOpenBlueModal] = useState<boolean>(false)

  const [generateTicketRightsResponse, generateTicketRights] = useGenerateTicketRights()
  const [patchStatus, patch] = useEmployeePatch({
    useToast: false,
    onSuccess: () => {
      generateTicketRights()
    },
  })
  const [customerPreferenceResponse, updateCustomerPreference] = useApi(patchCustomerPreference, {
    successCallback: (data) => {
      setCustomerPreferences(data)
      addToast({
        content: 'Billettype endret!',
      })
    },
  })
  const activeTicketRights = calculateActiveTicketRights(employee, category)

  const abstainFromTicket = () => {
    patch({
      abstainFromTicket: !employee?.abstainFromTicket,
    })
  }

  const setCustomerPreference = (ticketTypePreference: TicketTypePreference) => {
    updateCustomerPreference({
      id: `${employee?.customerNumber}`,
      body: { ticketTypePreference },
    })
  }

  const isChangeTicketTypeButtonEnabled =
    employee?.customerNumber && employee.customerPreferences && !isSeniorCitizenByAge(employee.dateOfBirth)

  const currentTicketTypePreference: TicketTypePreference =
    employee.customerPreferences?.ticketTypePreference ?? TicketTypePreference.ADULT

  return (
    <div style={{ marginBottom: space.extraLarge2 }}>
      <Heading3 style={{ marginBottom: space.extraSmall }} className={disabled ? 'disabled' : ''}>
        Rettigheter
        {!readOnly && hasSensitiveAccess === true && (
          <div style={{ display: 'inline-block', marginLeft: space.medium }}>
            <OverflowMenu
              button={
                <MenuBtn
                  style={{ display: 'inline-flex' }}
                  disabled={disabled}
                  data-cy="ticket-rights-menu-button"
                  id="ticket-rights-menu-btn"
                >
                  <HorizontalDotsIcon />
                </MenuBtn>
              }
              position="right"
            >
              <OverflowMenuItem
                data-cy="ticket-rights-menu-abstain-ticket-right"
                onSelect={() => {
                  abstainFromTicket()
                }}
              >
                {!employee.abstainFromTicket ? <ClosedLockIcon /> : <OpenedLockIcon />}
                {!employee.abstainFromTicket ? 'Sperr rettighet' : 'Åpne rettighet'}
              </OverflowMenuItem>
              {currentTicketTypePreference !== TicketTypePreference.SENIOR_CITIZEN && (
                <OverflowMenuItem
                  onSelect={() => setCustomerPreference(TicketTypePreference.SENIOR_CITIZEN)}
                  data-cy="ticket-rights-menu-change-ticket-type"
                  disabled={!isChangeTicketTypeButtonEnabled}
                >
                  <span aria-hidden>
                    <ValidTicketIcon inline={true} />
                  </span>
                  {'Sett billettype til Honnør'}
                </OverflowMenuItem>
              )}

              {currentTicketTypePreference !== TicketTypePreference.STUDENT && (
                <OverflowMenuItem
                  onSelect={() => setCustomerPreference(TicketTypePreference.STUDENT)}
                  data-cy="ticket-rights-menu-change-ticket-type"
                  disabled={!isChangeTicketTypeButtonEnabled}
                >
                  <span aria-hidden>
                    <ValidTicketIcon inline={true} />
                  </span>
                  {'Sett billettype til Student'}
                  &emsp;
                </OverflowMenuItem>
              )}
              {currentTicketTypePreference !== TicketTypePreference.ADULT && (
                <OverflowMenuItem
                  onSelect={() => setCustomerPreference(TicketTypePreference.ADULT)}
                  data-cy="ticket-rights-menu-change-ticket-type"
                  disabled={!isChangeTicketTypeButtonEnabled}
                >
                  <span aria-hidden>
                    <ValidTicketIcon inline={true} />
                  </span>
                  {'Sett billettype til Voksen'}
                  &emsp;
                </OverflowMenuItem>
              )}
              <OverflowMenuItem
                className="ticket-rights-menu-new-ticket-right"
                data-cy="ticket-rights-menu-new-ticket-right"
                onSelect={() => {
                  setOpenBlueModal(true)
                }}
              >
                <span aria-hidden>
                  <AddIcon inline={true} />
                </span>
                Legg til rettighet
              </OverflowMenuItem>
            </OverflowMenu>
          </div>
        )}
      </Heading3>
      {isOpenBlueModal && (
        <BlueModal setOpen={setOpenBlueModal} open={isOpenBlueModal}>
          <NewTicketRight employee={employee} setOpen={setOpenBlueModal} />
        </BlueModal>
      )}
      <SubParagraph style={{ marginBottom: space.extraSmall, marginTop: 0 }} data-cy="rights-info">
        <StrongText> Kode: </StrongText>
        {employee.ticketHolderCode} <CopyButton value={employee.ticketHolderCode} description="Kode" />
        &emsp;
        <StrongText> Billettype: </StrongText>
        {ticketTypeLabel(employee.customerPreferences)}&emsp;
        <StrongText> Sperret rettighet: </StrongText>
        {employee.abstainFromTicket ? 'Sperret' : 'Ikke sperret'}
      </SubParagraph>
      <GridContainer spacing="medium">
        {patchStatus === FetchState.ERROR && (
          <GridItem small={12}>
            <BannerAlertBox closable variant="error">
              Oppdatering av sperret rettighet feilet
            </BannerAlertBox>
          </GridItem>
        )}
        {customerPreferenceResponse.status === FetchState.ERROR && (
          <GridItem small={12}>
            <BannerAlertBox closable variant="error">
              Endring av billettype feilet
            </BannerAlertBox>
          </GridItem>
        )}
        {generateTicketRightsResponse.status === FetchState.ERROR && (
          <GridItem small={12}>
            <BannerAlertBox variant="error">Rettighet ble ikke generert på nytt.</BannerAlertBox>
          </GridItem>
        )}
        {(activeTicketRights?.length || 0) === 0 && !category && (
          <GridItem
            style={{
              backgroundColor: colors.greys.grey90,
              padding: space.small,
              color: colors.blues.blue10,
              fontStyle: 'italic',
            }}
            small={5}
          >
            Ingen nåværende rettigheter
          </GridItem>
        )}

        {activeTicketRights?.map((ticketRight) => (
          <GridItem small={6} key={ticketRight.id}>
            <TicketRightCard ticketRightWithDraft={ticketRight} />
          </GridItem>
        ))}
      </GridContainer>
    </div>
  )
}
